// lib/clients/fonts.ts
// Single source of truth for fonts the widget bundle can load per client.
//
// To add a new client font:
//   1. Drop the .woff2 files under public/fonts/widget/<font-key>/
//   2. Add an entry to FONT_REGISTRY below
//   3. Set `ui.fontFamily: '<font-key>'` on the client's config
// That's it — no code changes elsewhere.

export interface FontFile {
  /** A single weight (400) or, for a variable font, a range ("100 900"). */
  weight: number | string;
  /** Path relative to /public/fonts/widget/ */
  file: string;
  style?: 'normal' | 'italic';
  /** For subset files (one per script): the characters this file covers. */
  unicodeRange?: string;
}

export interface FontRegistryEntry {
  /** font-family name as declared in @font-face. Used in the CSS variable's value. */
  family: string;
  /** CSS variable that components consume (e.g. via Tailwind's `font-gotham` class). */
  cssVariable: string;
  /** Per-weight files relative to /public/fonts/widget/. */
  files: FontFile[];
  /** Generic fallbacks appended after the family name. */
  fallbackStack: string;
  /**
   * Optional CSS `size-adjust` for the @font-face (e.g. '104%'): draws the
   * glyphs larger/smaller without touching any component's font-size.
   */
  sizeAdjust?: string;
}

/**
 * Resolved font config sent to the widget. File paths are relative to the
 * widget origin (e.g. `gotham-greek/GothamGreek-Book.woff2`); the widget
 * bootstrap prefixes them with its derived `apiOrigin` at runtime so the same
 * config works whether the bundle is loaded same-origin or cross-origin.
 */
export interface ResolvedFont {
  key: string;
  family: string;
  cssVariable: string;
  fallbackStack: string;
  sizeAdjust?: string;
  files: Array<{ weight: number | string; file: string; style: 'normal' | 'italic'; unicodeRange?: string }>;
}

export const FONT_REGISTRY: Record<string, FontRegistryEntry> = {
  // The default since 2026-10-01: the same face as the Search Console
  // dashboard (founder: one font across the product). Free (OFL, licence in
  // public/fonts/widget/commissioner/OFL.txt), Greek-complete, VARIABLE — one
  // file per script covers every weight 100–900, so 500/600/700 are all real
  // cuts (Gotham had no 600: font-semibold used to render as Bold). Files from
  // @fontsource-variable/commissioner 5.3.0; unicode ranges as Google Fonts
  // splits them, so a page only downloads the scripts it actually shows.
  commissioner: {
    family: 'Commissioner',
    // Kept as --font-gotham-greek: 37 components reach the modal font through
    // the `font-gotham` utility, which maps to this variable. The name is
    // historical; the value is whichever font the client resolves to.
    cssVariable: '--font-gotham-greek',
    files: [
      { weight: '100 900', file: 'commissioner/Commissioner-greek.woff2', unicodeRange: 'U+0370-0377, U+037A-037F, U+0384-038A, U+038C, U+038E-03A1, U+03A3-03FF' },
      { weight: '100 900', file: 'commissioner/Commissioner-latin-ext.woff2', unicodeRange: 'U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF' },
      { weight: '100 900', file: 'commissioner/Commissioner-latin.woff2', unicodeRange: 'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD' },
    ],
    fallbackStack: 'Arial, Helvetica, sans-serif',
    // Drawn 4% larger: at the same font-size Commissioner's lowercase is ~4%
    // shorter and its text ~7% narrower than Gotham Greek (measured in the
    // browser on our own strings, 2026-10-01). 104% restores Gotham's x-height,
    // so the modal's type fills its space as it was designed to, with no
    // component size changed. Keep in sync with widget/src/widget.css and the
    // demo pages' font (app/layout.tsx).
    sizeAdjust: '104%',
  },
  // The modal's font until 2026-10-01. Kept so a client can opt back with
  // ui.fontFamily: 'gotham-greek'.
  'gotham-greek': {
    family: 'GothamGreek',
    cssVariable: '--font-gotham-greek',
    files: [
      { weight: 400, file: 'gotham-greek/GothamGreek-Book.woff2' },
      { weight: 500, file: 'gotham-greek/GothamGreek-Medium.woff2' },
      { weight: 700, file: 'gotham-greek/GothamGreek-Bold.woff2' },
    ],
    fallbackStack: 'Arial, Helvetica, sans-serif',
  },
};

/** Default font for clients that don't specify their own (Gotham Greek until 2026-10-01). */
export const DEFAULT_FONT_KEY = 'commissioner';

/**
 * Resolve a font key to its registry entry. Falls back to the default
 * (Commissioner) when no key is provided or the key isn't registered (with a warning).
 */
export function resolveFont(key: string | undefined): ResolvedFont {
  const requested = key && FONT_REGISTRY[key] ? key : DEFAULT_FONT_KEY;
  if (key && !FONT_REGISTRY[key]) {
    console.warn(`[fonts] unknown fontFamily "${key}", falling back to "${DEFAULT_FONT_KEY}"`);
  }
  const entry = FONT_REGISTRY[requested];
  return {
    key: requested,
    family: entry.family,
    cssVariable: entry.cssVariable,
    fallbackStack: entry.fallbackStack,
    ...(entry.sizeAdjust ? { sizeAdjust: entry.sizeAdjust } : {}),
    files: entry.files.map((f) => ({
      weight: f.weight,
      file: f.file,
      style: f.style ?? 'normal',
      ...(f.unicodeRange ? { unicodeRange: f.unicodeRange } : {}),
    })),
  };
}
